import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Container,
  Paper,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';
const roomStatusOptions = ['Available', 'Occupied', 'Maintenance'];
const acTypeOptions = ['Split AC', 'Central AC', 'No AC'];
const cancellationPolicies = [
  'Flexible',
  'Moderate',
  'Strict',
  'Non-refundable',
];

export default function AddRoom() {
  const [form, setForm] = useState({
    totalroom: '',
    type: '',
    acType: acTypeOptions[0],
    capacity: '',
    roomSize: '',
    status: 'Available',
    refundable: true,
    cancellationPolicy: cancellationPolicies[0],
    hotelId: '',
    wifipass: '',
  });

  const [hotels, setHotels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [images, setImages] = useState([]);
  const [loadingHotels, setLoadingHotels] = useState(true);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(true);
  const [floors, setFloors] = useState([]);
  const [floorQty, setFloorQty] = useState({});
  const [roomNumbers, setRoomNumbers] = useState({});
  const [errors, setErrors] = useState({});
  const url = `${config.API_BASE_URL}${config.PRODUCT_LIST_API}`;

  const company = JSON.parse(sessionStorage.getItem('companyDetails'));
  // Fetch Hotels
  useEffect(() => {
    const fetchHotels = async () => {
      const token = sessionStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetHotelsdropdown/${company?.id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error('Failed to fetch hotels');
        const data = await res.json();
        setHotels(data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch hotels');
      }
      setLoadingHotels(false);
    };
    fetchHotels();
  }, []);
  useEffect(() => {
    if (hotels.length > 0) {
      setForm((prev) => ({
        ...prev,
        hotelId: hotels[0].id, // first hotel auto select
      }));
    }
  }, [hotels]);

  // Fetch Room Types
  useEffect(() => {
    const fetchRoomTypes = async () => {
      const token = sessionStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetAllRoomType/${encodeURIComponent(company?.id)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error('Failed to fetch room types');
        const data = await res.json();
        console.log(data);
        setRoomTypes(data);
        if (data.length > 0) {
          setForm((prev) => ({
            ...prev,
            type: data[0].roomtype || data[0].Roomtype,
          }));
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch room types');
      }
      setLoadingRoomTypes(false);
    };
    fetchRoomTypes();
  }, []);

  // Fetch Floors
  useEffect(() => {
    if (!form.hotelId) return;
    const fetchFloors = async () => {
      const token = sessionStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetHotelFloor/${form.hotelId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error('Failed to fetch hotel floors');
        const data = await res.json();
        // Automatically set floor numbers
        const floorData = data.map((f, idx) => ({
          ...f,
          floor: idx + 1,
        }));
        setFloors(floorData);
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch hotel floors');
      }
    };
    fetchFloors();
  }, [form.hotelId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleQtyChange = (floorNumber, qty) => {
    const floorData = floors.find((f) => f.floor === Number(floorNumber));
    const maxRooms = floorData?.availableRoom || 0;
    const numericQty = Math.max(0, Math.min(Number(qty), maxRooms));

    setFloorQty((prev) => ({ ...prev, [floorNumber]: numericQty }));

    setRoomNumbers((prevRooms) => {
      const prevFloorRooms = prevRooms[floorNumber] || [];
      const newFloorRooms = Array(numericQty)
        .fill('')
        .map((_, i) => prevFloorRooms[i] || '');
      return { ...prevRooms, [floorNumber]: newFloorRooms };
    });
  };

  const handleRoomNumberChange = (floorNumber, index, value) => {
    setRoomNumbers((prev) => {
      const floorRooms = [...(prev[floorNumber] || [])];
      floorRooms[index] = value;
      return { ...prev, [floorNumber]: floorRooms };
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).map((file) => ({ file }));
    setImages((prev) => [...prev, ...files]);
  };

  const handleDeleteImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errs = {};
    if (!form.hotelId) errs.hotelId = 'Hotel is required';
    if (!form.totalroom || Number(form.totalroom) <= 0)
      errs.totalroom = 'Valid Total Room count is required';
    if (!form.type) errs.type = 'Room Type is required';
    if (!form.capacity || Number(form.capacity) <= 0)
      errs.capacity = 'Valid Capacity is required';
    if (!form.roomSize || Number(form.roomSize) <= 0)
      errs.roomSize = 'Valid Room Size is required';
    if (!form.status) errs.status = 'Status is required';
    if (!form.cancellationPolicy)
      errs.cancellationPolicy = 'Cancellation Policy is required';
    if (images.length === 0) errs.images = 'At least one image is required';

    Object.keys(roomNumbers).forEach((floorNumber) => {
      const nums = roomNumbers[floorNumber];
      if (nums.some((n) => !n)) {
        errs[`roomNumbers_${floorNumber}`] =
          'All room numbers are required for this floor';
      }
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const resetForm = () => {
    setForm({
      totalroom: '',
      type:
        roomTypes.length > 0
          ? roomTypes[0].roomtype || roomTypes[0].Roomtype
          : '',
      acType: acTypeOptions[0],
      capacity: '',
      roomSize: '',
      status: 'Available',
      refundable: true,
      cancellationPolicy: cancellationPolicies[0],
      hotelId: '',
    });
    setImages([]);
    setErrors({});
    setFloors([]);
    setFloorQty({});
    setRoomNumbers({});
  };

  const handleSubmit = async (e) => {
    debugger;
    e.preventDefault();
    if (!validate()) {
      toast.error('Please correct the errors before submitting');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const formData = new FormData();

      formData.append('Price', '0');
      formData.append('Tax', '0');
      formData.append('OtherCharges', '0');
      formData.append('Inclusions', 'only room');
      formData.append('Description', 'only room');

      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });

      formData.append('FloorRooms', JSON.stringify(floorQty));
      formData.append('RoomNumbers', JSON.stringify(roomNumbers));

      images.forEach((imgObj) => formData.append('Images', imgObj.file));
      console.log(formData);
      const response = await fetch(`${config.API_BASE_URL}/Home/SaveRoom`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error('Error: ' + (errorData.message || response.statusText));
        return;
      }

      toast.success('Room added successfully!');
      window.location.reload();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error('Error saving room.');
    }
  };

  if (loadingHotels || loadingRoomTypes) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='50vh'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth='md' sx={{ mt: 4, mb: 6 }}>
      <ToastContainer position='top-center' autoClose={3000} />
      <Typography variant='h5' mb={3} fontWeight='bold' textAlign='center'>
        Add New Room
      </Typography>

      <Box
        component='form'
        noValidate
        onSubmit={handleSubmit}
        autoComplete='off'>
        {/* Hotel */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <TextField
            label='Hotel'
            value={hotels[0]?.HotelName || hotels[0]?.hotelName || ''}
            fullWidth
            disabled
          />
        </Paper>
        {/* Room Type */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <TextField
            select
            label='Room Type'
            name='type'
            value={form.type}
            onChange={handleChange}
            error={!!errors.type}
            helperText={errors.type}
            fullWidth
            required>
            {roomTypes.map((rt) => (
              <MenuItem key={rt.id || rt.ID} value={rt.roomtype || rt.Roomtype}>
                {rt.roomtype || rt.Roomtype}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* Other Fields */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Total Rooms'
                name='totalroom'
                type='number'
                value={form.totalroom}
                onChange={handleChange}
                error={!!errors.totalroom}
                helperText={errors.totalroom}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Capacity'
                name='capacity'
                type='number'
                value={form.capacity}
                onChange={handleChange}
                error={!!errors.capacity}
                helperText={errors.capacity}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Room Size (sq ft)'
                name='roomSize'
                type='number'
                value={form.roomSize}
                onChange={handleChange}
                error={!!errors.roomSize}
                helperText={errors.roomSize}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Wifi Password'
                name='wifipass'
                type='text'
                value={form.wifipass}
                onChange={handleChange}
                error={!!errors.wifipass}
                helperText={errors.wifipass}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.refundable}
                    onChange={handleChange}
                    name='refundable'
                  />
                }
                label='Refundable'
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Floor Wise Allocation */}
        {floors.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography variant='h6' mb={2} fontWeight='bold'>
              Floor Wise Room Allocation
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Floor</TableCell>
                  <TableCell>Total Rooms</TableCell>
                  <TableCell>Used</TableCell>
                  <TableCell>Available</TableCell>
                  <TableCell>Allocate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {floors.map((f) => {
                  const floorNumber = f.floor;
                  const allocated = floorQty[floorNumber] || 0;
                  return (
                    <TableRow key={floorNumber}>
                      <TableCell>{f.floor}</TableCell>
                      <TableCell>{f.totalRooms}</TableCell>
                      <TableCell>{f.usedRoom}</TableCell>
                      <TableCell>{f.availableRoom}</TableCell>
                      <TableCell>
                        <TextField
                          type='number'
                          size='small'
                          value={allocated}
                          onChange={(e) =>
                            handleQtyChange(floorNumber, e.target.value)
                          }
                          inputProps={{ min: 0, max: f.availableRoom }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Room Numbers */}
            {Object.keys(floorQty).map((floorNumber) => {
              const qty = floorQty[floorNumber];
              if (!qty || qty <= 0) return null;
              return (
                <Box key={floorNumber} mt={2}>
                  <Typography fontWeight='bold'>
                    Floor {floorNumber} - {form.type} Room Numbers
                  </Typography>
                  <Grid container spacing={2} mt={1}>
                    {Array.from({ length: qty }).map((_, idx) => (
                      <Grid item xs={12} sm={3} key={idx}>
                        <TextField
                          label={`Room #${idx + 1}`}
                          size='small'
                          value={(roomNumbers[floorNumber] || [])[idx] || ''}
                          onChange={(e) =>
                            handleRoomNumberChange(
                              floorNumber,
                              idx,
                              e.target.value,
                            )
                          }
                          error={!!errors[`roomNumbers_${floorNumber}`]}
                          fullWidth
                          helperText='Number ya naam (e.g. 101 ya Maharaja Room)'
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {errors[`roomNumbers_${floorNumber}`] && (
                    <Typography color='error'>
                      {errors[`roomNumbers_${floorNumber}`]}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Paper>
        )}

        {/* Images */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant='h6' mb={2} fontWeight='bold'>
            Upload Images
          </Typography>
          <Button
            variant='contained'
            component='label'
            startIcon={<PhotoCamera />}
            fullWidth>
            Upload Images
            <input
              hidden
              type='file'
              accept='image/*'
              multiple
              onChange={handleImageChange}
            />
          </Button>
          {errors.images && (
            <Typography color='error'>{errors.images}</Typography>
          )}
          {images.length > 0 && (
            <Box
              sx={{ display: 'flex', overflowX: 'auto', gap: 2, py: 2, mt: 2 }}>
              {images.map((imgObj, idx) => {
                if (!imgObj?.file) return null;
                const url = URL.createObjectURL(imgObj.file);
                return (
                  <Box
                    key={idx}
                    sx={{
                      position: 'relative',
                      minWidth: 100,
                      width: 100,
                      height: 100,
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}>
                    <img
                      src={url}
                      alt={`Room ${idx}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onLoad={() => URL.revokeObjectURL(url)}
                    />
                    <IconButton
                      size='small'
                      color='error'
                      onClick={() => handleDeleteImage(idx)}
                      sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        background: 'white',
                      }}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        <Button
          type='submit'
          variant='contained'
          fullWidth
          sx={{ mt: 2, py: 1.5 }}>
          Add Room
        </Button>
      </Box>
    </Container>
  );
}
